import React, { useState, useEffect, useRef, useCallback } from "react";
import { useMediaQuery } from "../../Components";
import { arrowRight, Toast } from "../../Components";
import { YearToDate } from "../../Components";
import DashboardItem from "./DashboardItem";
import BrandNotificationItem from "./BrandNotificationItem";
import { campaignService } from "../../Services";
import { storageHandler, utils } from "../../Helpers";
import { InfoPage1 } from "../../Components/InfoPage1";
import { brandService } from "../../Services/brand.service";
import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";
import {
  Table,
  TableBody,
  TableHead,
  TableCell,
  TableRow,
} from "@mui/material";
import { Download } from "lucide-react";
import NotSubscribe from "../../Components/Base/Common/NotSubscribe";

const ytdList = [
  { id: "0", value: "Year to date" },
  { id: "1", value: "Past 1 month" },
  { id: "6", value: "Past 6 months" },
  { id: "12", value: "Past 12 months" },
];

const BrandDashboardShimmer = () => {
  return (
    <div
      className="dashboard"
      style={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        backgroundColor: "#fafffa",
        padding: "0"
      }}
    >
      <div
        className="dashboard__inner"
        style={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {/* Top Cards Grid */}
        <div
          className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4 w-full mb-4"
          style={{ alignItems: "stretch", flexShrink: 0 }}
        >
          {/* Campaign Status Card Shimmer */}
          <div
            className="lg:col-span-2 xl:col-span-1"
            style={{
              background: "linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(240,253,244,0.9) 100%)",
              borderRadius: "16px",
              border: "1px solid rgba(16, 185, 129, 0.15)",
              padding: "16px",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-gray-200 animate-pulse" />
                <div className="h-4 w-32 rounded bg-gray-200 animate-pulse" />
              </div>
            </div>
            <div className="flex flex-col lg:flex-row items-center gap-3">
              <div className="w-24 h-24 rounded-full bg-gray-200 animate-pulse flex-shrink-0" />
              <div className="flex flex-col gap-1.5 flex-1 w-full">
                <div className="h-6 w-full rounded-lg bg-gray-200 animate-pulse" />
                <div className="h-6 w-full rounded-lg bg-gray-200 animate-pulse" />
                <div className="h-6 w-3/4 rounded-lg bg-gray-200 animate-pulse" />
                <div className="h-6 w-1/2 rounded-lg bg-gray-200 animate-pulse" />
              </div>
            </div>
          </div>

          {/* Offers Card Shimmer */}
          <div
            className="lg:col-span-2 xl:col-span-1"
            style={{
              background: "linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(236,253,245,0.9) 100%)",
              borderRadius: "16px",
              border: "1px solid rgba(52, 211, 153, 0.15)",
              padding: "16px",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-gray-200 animate-pulse" />
                <div className="h-4 w-40 rounded bg-gray-200 animate-pulse" />
              </div>
            </div>
            <div className="flex flex-col lg:flex-row items-center gap-3">
              <div className="w-24 h-24 rounded-full bg-gray-200 animate-pulse flex-shrink-0" />
              <div className="flex flex-col gap-1.5 flex-1 w-full">
                <div className="h-6 w-full rounded-lg bg-gray-200 animate-pulse" />
                <div className="h-6 w-full rounded-lg bg-gray-200 animate-pulse" />
                <div className="h-6 w-3/4 rounded-lg bg-gray-200 animate-pulse" />
                <div
                  className="h-7 w-full rounded-lg bg-gray-200 animate-pulse mt-1"
                  style={{
                    borderTop: "1px solid rgba(16, 185, 129, 0.2)",
                    paddingTop: "6px",
                  }}
                />
              </div>
            </div>
          </div>

          {/* Outstanding Commitments Card Shimmer */}
          <div
            className="lg:col-span-2 xl:col-span-1"
            style={{
              background:
                "linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(236,253,245,0.9) 100%)",
              borderRadius: "16px",
              border: "1px solid rgba(6, 182, 212, 0.15)",
              padding: "16px",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-gray-200 animate-pulse" />
                <div className="h-4 w-44 rounded bg-gray-200 animate-pulse" />
              </div>
            </div>
            <div className="flex flex-col gap-2 mt-1">
              <div
                className="h-11 w-full rounded-lg bg-gray-200 animate-pulse"
                style={{ border: "1px solid rgba(6, 182, 212, 0.1)" }}
              />
              <div
                className="h-11 w-full rounded-lg bg-gray-200 animate-pulse"
                style={{ border: "1px solid rgba(16, 185, 129, 0.1)" }}
              />
              <div
                className="h-11 w-full rounded-lg bg-gray-200 animate-pulse"
                style={{ border: "1px solid rgba(139, 92, 246, 0.1)" }}
              />
            </div>
          </div>
        </div>

        {/* Bottom Section Grid */}
        <div
          className="grid grid-cols-1 sm:grid-cols-1 md:grid-cols-1 lg:grid-cols-3 xl:grid-cols-3 2xl:grid-cols-3 gap-4 w-full"
          style={{
            flex: 1,
            minHeight: 0,
            overflow: "hidden",
          }}
        >
          {/* Main Content (Table Area) */}
          <div
            className="lg:col-span-2 xl:col-span-2 2xl:col-span-2 flex flex-col"
            style={{
              background:
                "linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(240,253,244,0.9) 100%)",
              borderRadius: "16px",
              border: "1px solid rgba(16, 185, 129, 0.15)",
              paddingTop: "16px",
              height: "100%",
              overflow: "hidden",
            }}
          >
            <div className="flex justify-between items-center px-4 mb-3">
              <div className="h-5 w-28 rounded bg-gray-200 animate-pulse" />
              <div className="flex gap-2">
                <div className="h-8 w-28 rounded-2xl bg-gray-200 animate-pulse" />
                <div className="h-8 w-32 rounded-2xl bg-gray-200 animate-pulse" />
              </div>
            </div>
            
            <div
              style={{
                background: "#fff",
                boxShadow: "0px 1px 3px 0px rgba(16, 24, 40, 0.08)",
                border: "1px solid rgba(16, 185, 129, 0.1)",
                flex: 1,
                overflow: "hidden",
              }}
            >
              {/* Table Header Skeleton */}
              <div
                className="flex items-center px-4 py-2.5"
                style={{
                  background:
                    "linear-gradient(180deg, #F8FAFC 0%, #F1F5F9 100%)",
                  borderBottom: "2px solid #E2E8F0",
                }}
              >
                <div className="h-3 w-1/4 rounded bg-gray-200 animate-pulse mr-4" />
                <div className="h-3 w-1/5 rounded bg-gray-200 animate-pulse mr-4" />
                <div className="h-3 w-1/3 rounded bg-gray-200 animate-pulse mr-4" />
                <div className="h-3 w-1/6 rounded bg-gray-200 animate-pulse" />
              </div>

              {/* Table Rows Skeleton - Reduced to 4 rows */}
              <div className="flex flex-col">
                {[1, 2, 3, 4].map((row) => (
                  <div
                    key={row}
                    className="flex items-center px-4 py-2.5"
                    style={{
                      borderBottom: "1px solid #F3F4F6",
                      backgroundColor:
                        row % 2 === 0
                          ? "rgba(248, 250, 252, 0.5)"
                          : "transparent",
                    }}
                  >
                    <div className="h-3 w-1/4 rounded bg-gray-200 animate-pulse mr-4" />
                    <div className="h-3 w-1/5 rounded bg-gray-200 animate-pulse mr-4" />
                    <div className="h-3 w-1/3 rounded bg-gray-200 animate-pulse mr-4" />
                    <div className="h-3 w-1/6 rounded bg-gray-200 animate-pulse" />
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right Sidebar (Notifications) */}
          <div
            className="flex flex-col"
            style={{
              background: "white",
              borderRadius: "16px",
              border: "1px solid rgba(52, 211, 153, 0.15)",
              height: "100%",
              overflow: "hidden",
            }}
          >
            <div
              className="flex justify-between items-center px-4"
              style={{
                background:
                  "linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(240,253,244,0.9) 100%)",
                paddingTop: "16px",
                paddingBottom: "8px",
              }}
            >
              <div className="h-5 w-28 rounded bg-gray-200 animate-pulse" />
              <div className="h-4 w-16 rounded bg-gray-200 animate-pulse" />
            </div>

            {/* Reduced to 3 notification items */}
            <div className="flex flex-col gap-3 p-4">
              {[1, 2, 3].map((item) => (
                <div
                  key={item}
                  className="flex gap-3 p-3"
                  style={{
                    border: "1px solid rgba(16, 185, 129, 0.1)",
                    borderRadius: "12px",
                  }}
                >
                  <div className="w-10 h-10 rounded-full bg-gray-200 animate-pulse flex-shrink-0" />
                  <div className="flex flex-col gap-2 flex-1">
                    <div className="h-4 w-3/4 rounded bg-gray-200 animate-pulse" />
                    <div className="h-3 w-full rounded bg-gray-200 animate-pulse" />
                    <div className="h-3 w-1/2 rounded bg-gray-200 animate-pulse" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default function BrandDashboard({
  NotifModul,
  setNotifModul,
  deleteNotif,
  user,
  profiles,
  setModalSwitch,
  updateNotificationStatus,
  setModalNotification,
  setModalNotificationData,
  accountCountNotification,
  closeCartPanel,
  cartItem,
  goToPricing,
  setModal,
  setAuth,
}) {
  // eslint-disable-next-line
  const [dateRange, setDateRange] = useState(0);
  const [dashboardSummary, setDashboardSummary] = useState({});
  const [dashboardCampaign, setDashboardCampaign] = useState([]);
  const [isloaded, setIsLoaded] = useState(false);
  const [paginationData, setPaginationData] = useState({});
  const [page, setPage] = useState(1);
  const [sortOrder, setSortOrder] = useState("desc");
  const [isCampaignLoaded, setIsCampaignLoaded] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const tableContainerRef = useRef(null);
  const [toastList, setToastList] = useState([]);
  const [modalData, setModalData] = useState(null);
  const [show, setShow] = useState(!true);
  const influencerName = user ? (user.data ? user.data.firstName : null) : null;
  const width = useMediaQuery();
  const [brandSummaryData, setBrandSummaryData] = useState();
  const [isUserHasSubscription, setIsUserHasSubscription] = useState();

  useEffect(() => {
    async function fetchBrandDashboardData() {
      try {
        setIsCampaignLoaded(false);
        const res = await campaignService.getAll({
          page: page,
          pageSize: 5,
          range: dateRange,
          sortBy: sortOrder,
        });

        setDashboardSummary(res.summary || {});

        if (page === 1) {
          setDashboardCampaign(res.data || []);
        } else {
          setDashboardCampaign((prev) => [...prev, ...(res.data || [])]);
        }

        if (res.meta?.pagination) {
          setPaginationData(res.meta.pagination);
          setHasMore(
            res.meta.pagination.current_page < res.meta.pagination.total_pages
          );
        }
      } catch (err) {
        setDashboardSummary({
          totalactivecampaign: 0,
          totalrevenueofactivecampaign: "$0",
          availablebalance: "$0",
        });
        if (page === 1) {
          setDashboardCampaign([]);
        }
        setPaginationData({});
      } finally {
        setIsCampaignLoaded(true);
        setIsLoaded(true);
        setIsLoadingMore(false);
      }
    }

    fetchBrandDashboardData();
  }, [page, dateRange, sortOrder]);

  const fetchDashboradSummary = async () => {
    const brandSummary = await brandService.getBrandDashboardSummary();
    if (brandSummary?.success) {
      setBrandSummaryData(brandSummary?.data);
    }
  };

  useEffect(() => {
    fetchDashboradSummary();
  }, []);

  const downloadCsv = async () => {
    try {
      const response = await campaignService.getAll({
        page: page,
        pageSize: 5,
        range: dateRange,
        fileType: "csv",
        sortBy: sortOrder,
      });
      utils.downloadFile(response, "brandDashboard_page_" + page + ".csv");
    } catch (err) {
      addToast({
        error: true,
        msg: err,
        id: `${Date.now()}-${toastList.length}`,
      });
    }
  };

  const handleScroll = useCallback(() => {
    if (!tableContainerRef.current || isLoadingMore || !hasMore) return;

    const { scrollTop, scrollHeight, clientHeight } = tableContainerRef.current;
    if (scrollTop + clientHeight >= scrollHeight - 10) {
      setIsLoadingMore(true);
      setPage((prev) => prev + 1);
    }
  }, [isLoadingMore, hasMore]);

  const sortList = () => {
    setSortOrder(sortOrder === "desc" ? "asc" : "desc");
  };

  const addToast = (data) => {
    const newToastList = [...toastList];
    newToastList.push({ ...data, id: `${Date.now()}-${toastList.length}` });
    setToastList(newToastList);
  };

  const maxPlaceholder = width <= 1180 ? 2 : 3;
  const NotifMax = width <= 1180 && NotifModul.length > 2 ? 2 : 3;
  const NotifMaxPlaceholder = width <= 1180 ? 2 : 3;
  const showNotifPlacholder = NotifMaxPlaceholder - NotifModul.length;

  const changeMedium = (item) => {
    setDateRange(item.id);
    setPage(1);
    setSortOrder("desc");
    setDashboardCampaign([]);
    setHasMore(true);
  };

  // Transform campaign data for pie chart
  const getCampaignChartData = () => {
    if (!brandSummaryData?.campaign) {
      return [{ name: "No campaigns available.", value: 1, color: "#10B981" }];
    }
    const colors = ["#059669", "#10B981", "#34D399", "#6EE7B7"];
    const data = Object.entries(brandSummaryData.campaign).map(
      ([key, value], index) => ({
        name: key.replace("_", " ").toUpperCase(),
        value: value,
        color: colors[index % colors.length],
      })
    );
    const hasData = data.some((item) => item.value > 0);
    return hasData
      ? data
      : [{ name: "No campaigns available.", value: 1, color: "#10B981" }];
  };

  // Transform offers data for pie chart
  const getOffersChartData = () => {
    if (!brandSummaryData?.offers_last_90_days) {
      return [{ name: "No Offers yet", value: 1, color: "#10B981" }];
    }
    const colors = ["#059669", "#10B981", "#34D399", "#6EE7B7"];
    const data = Object.entries(brandSummaryData.offers_last_90_days)
      .filter(([key]) => key !== "total")
      .map(([key, value], index) => ({
        name: key.replace("_", " ").toUpperCase(),
        value: value,
        color: colors[index % colors.length],
      }));
    const hasData = data.some((item) => item.value > 0);
    return hasData
      ? data
      : [{ name: "No Offers yet", value: 1, color: "#10B981" }];
  };

  useEffect(() => {
     try {
      let data = ""
       const userInfo = storageHandler.getUserInfo();
       if (userInfo) {
         const parsed = JSON.parse(userInfo);
         data = parsed?.data || null;
       }
       setIsUserHasSubscription(data?.isUserHasSubscription)
     } catch (error) {
       console.error("Error parsing user info:", error);
     }
  }, []);

  return (
    <>
      {isUserHasSubscription ? (
        <>
          <div className="main">
            <div>
              {/* Replaced LoadingBackDrop with Shimmer */}
              {!isloaded ? (
                <BrandDashboardShimmer />
              ) : show ? (
                <InfoPage1
                  onClose={() => setShow(false)}
                  title={`Hey ${influencerName}!`}
                  description={`Lorem ipsum dolor sit amet consectetur adipisicing elit. Fuga dolore, culpa assumenda harum inventore quidem, rerum nostrum nihil debitis laboriosam eaque autem veniam sunt itaque.`}
                  img={`https://images.unsplash.com/photo-1499951360447-b19be8fe80f5?q=80&w=1470&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D`}
                />
              ) : (
                <div
                  className="dashboard"
                  style={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    overflow: "hidden",
                    backgroundColor: "#fafffa",
                  }}
                >
                  <div
                    className="dashboard__inner"
                    style={{
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      overflow: "hidden",
                    }}
                  >
                    {/* Charts Grid */}
                    <div
                      className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4 w-full mb-4"
                      style={{ alignItems: "stretch" }}
                    >
                      {/* Campaign Status Chart */}
                      <div
                        className="lg:col-span-2 xl:col-span-1 group"
                        style={{
                          background:
                            "linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(240,253,244,0.9) 100%)",
                          backdropFilter: "blur(20px)",
                          WebkitBackdropFilter: "blur(20px)",
                          borderRadius: "16px",
                          border: "1px solid rgba(16, 185, 129, 0.15)",
                          padding: "16px",
                          transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                          position: "relative",
                          overflow: "hidden",
                          display: "flex",
                          flexDirection: "column",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = "translateY(-4px)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = "translateY(0)";
                        }}
                      >
                        {/* Decorative gradient orb */}
                        <div
                          style={{
                            position: "absolute",
                            top: "-50px",
                            right: "-50px",
                            width: "150px",
                            height: "150px",
                            // background:
                            //   "radial-gradient(circle, rgba(16, 185, 129, 0.15) 0%, transparent 70%)",
                            borderRadius: "50%",
                            pointerEvents: "none",
                          }}
                        />
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <div
                              style={{
                                width: "32px",
                                height: "32px",
                                background:
                                  "linear-gradient(135deg, #10B981 0%, #059669 100%)",
                                borderRadius: "10px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <svg
                                width="16"
                                height="16"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="white"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <path d="M21.21 15.89A10 10 0 1 1 8 2.83" />
                                <path d="M22 12A10 10 0 0 0 12 2v10z" />
                              </svg>
                            </div>
                            <h6
                              style={{
                                fontSize: "14px",
                                fontWeight: "700",
                                background:
                                  "linear-gradient(135deg, #059669 0%, #047857 100%)",
                                WebkitBackgroundClip: "text",
                                WebkitTextFillColor: "transparent",
                                backgroundClip: "text",
                                margin: 0,
                              }}
                            >
                              Campaign Status
                            </h6>
                          </div>
                        </div>
                        <div className="flex flex-col lg:flex-row items-center gap-3">
                          <div style={{ width: "100px", height: "100px" }}>
                            <ResponsiveContainer width="100%" height="100%">
                              <PieChart>
                                <Pie
                                  data={getCampaignChartData()}
                                  cx="50%"
                                  cy="50%"
                                  innerRadius={25}
                                  outerRadius={45}
                                  paddingAngle={3}
                                  dataKey="value"
                                >
                                  {getCampaignChartData().map(
                                    (entry, index) => (
                                      <Cell
                                        key={`cell-${index}`}
                                        fill={entry.color}
                                        stroke="white"
                                        strokeWidth={2}
                                      />
                                    )
                                  )}
                                </Pie>
                              </PieChart>
                            </ResponsiveContainer>
                          </div>
                          <div
                            style={{
                              flex: 1,
                              display: "flex",
                              flexDirection: "column",
                              gap: "6px",
                            }}
                          >
                            {getCampaignChartData().map((item, index) => (
                              <div
                                key={index}
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                  padding: "4px 8px",
                                  background: "rgba(255,255,255,0.6)",
                                  borderRadius: "8px",
                                  transition: "all 0.3s ease",
                                }}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "10px",
                                  }}
                                >
                                  <div
                                    style={{
                                      width: "10px",
                                      height: "10px",
                                      borderRadius: "50%",
                                      backgroundColor: item.color,
                                      boxShadow: `0 2px 8px ${item.color}50`,
                                    }}
                                  />
                                  <span
                                    style={{
                                      fontSize: "12px",
                                      color: "#374151",
                                      fontWeight: "500",
                                    }}
                                  >
                                    {item.name}
                                  </span>
                                </div>
                                <span
                                  style={{
                                    fontSize: "12px",
                                    fontWeight: "700",
                                    color: "#111827",
                                  }}
                                >
                                  {item.name === "No campaigns available." ||
                                  item.name === "No Offers yet"
                                    ? "0"
                                    : item.value}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>

                      {/* Offers Chart */}
                      <div
                        className="lg:col-span-2 xl:col-span-1 group"
                        style={{
                          background:
                            "linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(236,253,245,0.9) 100%)",
                          backdropFilter: "blur(20px)",
                          WebkitBackdropFilter: "blur(20px)",
                          borderRadius: "16px",
                          border: "1px solid rgba(52, 211, 153, 0.15)",
                          padding: "16px",
                          transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                          position: "relative",
                          overflow: "hidden",
                          display: "flex",
                          flexDirection: "column",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = "translateY(-4px)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = "translateY(0)";
                        }}
                      >
                        {/* Decorative gradient orb */}
                        <div
                          style={{
                            position: "absolute",
                            top: "-50px",
                            right: "-50px",
                            width: "120px",
                            height: "120px",
                            background:
                              "radial-gradient(circle, rgba(52, 211, 153, 0.15) 0%, transparent 70%)",
                            borderRadius: "50%",
                            pointerEvents: "none",
                          }}
                        />
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <div
                              style={{
                                width: "32px",
                                height: "32px",
                                background:
                                  "linear-gradient(135deg, #34D399 0%, #10B981 100%)",
                                borderRadius: "10px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <svg
                                width="16"
                                height="16"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="white"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
                              </svg>
                            </div>
                            <h6
                              style={{
                                fontSize: "14px",
                                fontWeight: "700",
                                background:
                                  "linear-gradient(135deg, #10B981 0%, #059669 100%)",
                                WebkitBackgroundClip: "text",
                                WebkitTextFillColor: "transparent",
                                backgroundClip: "text",
                                margin: 0,
                              }}
                            >
                              Offers (Last 90 Days)
                            </h6>
                          </div>
                        </div>
                        <div className="flex flex-col lg:flex-row items-center justify-between gap-3">
                          <div style={{ width: "100px", height: "100px" }}>
                            <ResponsiveContainer width="100%" height="100%">
                              <PieChart>
                                <Pie
                                  data={getOffersChartData()}
                                  cx="50%"
                                  cy="50%"
                                  innerRadius={25}
                                  outerRadius={45}
                                  paddingAngle={3}
                                  dataKey="value"
                                >
                                  {getOffersChartData().map((entry, index) => (
                                    <Cell
                                      key={`cell-${index}`}
                                      fill={entry.color}
                                      stroke="white"
                                      strokeWidth={2}
                                    />
                                  ))}
                                </Pie>
                              </PieChart>
                            </ResponsiveContainer>
                          </div>
                          <div
                            style={{
                              flex: 1,
                              display: "flex",
                              flexDirection: "column",
                              gap: "6px",
                            }}
                          >
                            {getOffersChartData().map((item, index) => (
                              <div
                                key={index}
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                  padding: "4px 8px",
                                  background: "rgba(255,255,255,0.6)",
                                  borderRadius: "8px",
                                  transition: "all 0.3s ease",
                                }}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "8px",
                                  }}
                                >
                                  <div
                                    style={{
                                      width: "8px",
                                      height: "8px",
                                      borderRadius: "50%",
                                      backgroundColor: item.color,
                                      boxShadow: `0 2px 6px ${item.color}50`,
                                    }}
                                  />
                                  <span
                                    style={{
                                      fontSize: "12px",
                                      color: "#374151",
                                      fontWeight: "500",
                                    }}
                                  >
                                    {item.name}
                                  </span>
                                </div>
                                <span
                                  style={{
                                    fontSize: "14px",
                                    fontWeight: "700",
                                    color: "#111827",
                                  }}
                                >
                                  {item.name === "No campaigns available." ||
                                  item.name === "No Offers yet"
                                    ? "0"
                                    : item.value}
                                </span>
                              </div>
                            ))}
                            {brandSummaryData?.offers_last_90_days?.total ? (
                              <div
                                style={{
                                  paddingTop: "12px",
                                  borderTop:
                                    "1px solid rgba(16, 185, 129, 0.2)",
                                  marginTop: "4px",
                                }}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                  }}
                                >
                                  <span
                                    style={{
                                      fontSize: "13px",
                                      fontWeight: "600",
                                      color: "#059669",
                                    }}
                                  >
                                    Total
                                  </span>
                                  <span
                                    style={{
                                      fontSize: "16px",
                                      fontWeight: "800",
                                      color: "#047857",
                                    }}
                                  >
                                    {brandSummaryData?.offers_last_90_days
                                      ?.total || 0}
                                  </span>
                                </div>
                              </div>
                            ) : null}
                          </div>
                        </div>
                      </div>

                      {/* Outstanding Commitments */}
                      <div
                        className="lg:col-span-2 xl:col-span-1 group"
                        style={{
                          background:
                            "linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(236,253,245,0.9) 100%)",
                          backdropFilter: "blur(20px)",
                          WebkitBackdropFilter: "blur(20px)",
                          borderRadius: "16px",
                          border: "1px solid rgba(6, 182, 212, 0.15)",
                          padding: "16px",
                          transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                          position: "relative",
                          overflow: "hidden",
                          display: "flex",
                          flexDirection: "column",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = "translateY(-4px)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = "translateY(0)";
                        }}
                      >
                        {/* Decorative gradient orb */}
                        <div
                          style={{
                            position: "absolute",
                            top: "-50px",
                            right: "-50px",
                            width: "120px",
                            height: "120px",
                            background:
                              "radial-gradient(circle, rgba(6, 182, 212, 0.15) 0%, transparent 70%)",
                            borderRadius: "50%",
                            pointerEvents: "none",
                          }}
                        />
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <div
                              style={{
                                width: "32px",
                                height: "32px",
                                background:
                                  "linear-gradient(135deg, #06B6D4 0%, #0891B2 100%)",
                                borderRadius: "10px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <svg
                                width="16"
                                height="16"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="white"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <rect
                                  x="2"
                                  y="5"
                                  width="20"
                                  height="14"
                                  rx="2"
                                />
                                <line x1="2" y1="10" x2="22" y2="10" />
                              </svg>
                            </div>
                            <h6
                              style={{
                                fontSize: "14px",
                                fontWeight: "700",
                                background:
                                  "linear-gradient(135deg, #0891B2 0%, #0E7490 100%)",
                                WebkitBackgroundClip: "text",
                                WebkitTextFillColor: "transparent",
                                backgroundClip: "text",
                                margin: 0,
                              }}
                            >
                              Outstanding Commitments
                            </h6>
                          </div>
                        </div>
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "8px",
                            marginTop: "4px",
                          }}
                        >
                          <div
                            style={{
                              padding: "10px 12px",
                              background:
                                "linear-gradient(135deg, rgba(6, 182, 212, 0.08) 0%, rgba(6, 182, 212, 0.03) 100%)",
                              borderRadius: "10px",
                              border: "1px solid rgba(6, 182, 212, 0.1)",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              transition: "all 0.3s ease",
                            }}
                          >
                            <p
                              style={{
                                fontSize: "11px",
                                color: "#4B5563",
                                fontWeight: "600",
                                textTransform: "uppercase",
                                letterSpacing: "0.5px",
                                margin: 0,
                              }}
                            >
                              Count
                            </p>
                            <p
                              style={{
                                fontSize: "16px",
                                fontWeight: "800",
                                color: "#0891B2",
                                margin: 0,
                              }}
                            >
                              {brandSummaryData?.outstanding_commitments
                                ?.count || 0}
                            </p>
                          </div>
                          <div
                            style={{
                              padding: "10px 12px",
                              background:
                                "linear-gradient(135deg, rgba(16, 185, 129, 0.08) 0%, rgba(16, 185, 129, 0.03) 100%)",
                              borderRadius: "10px",
                              border: "1px solid rgba(16, 185, 129, 0.1)",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              transition: "all 0.3s ease",
                            }}
                          >
                            <p
                              style={{
                                fontSize: "11px",
                                color: "#4B5563",
                                fontWeight: "600",
                                textTransform: "uppercase",
                                letterSpacing: "0.5px",
                                margin: 0,
                              }}
                            >
                              Direct Pay
                            </p>
                            <p
                              style={{
                                fontSize: "16px",
                                fontWeight: "800",
                                color: "#059669",
                                margin: 0,
                              }}
                            >
                              $
                              {brandSummaryData?.outstanding_commitments?.direct_pay_total?.toLocaleString() ||
                                0}
                            </p>
                          </div>
                          <div
                            style={{
                              padding: "10px 12px",
                              background:
                                "linear-gradient(135deg, rgba(139, 92, 246, 0.08) 0%, rgba(139, 92, 246, 0.03) 100%)",
                              borderRadius: "10px",
                              border: "1px solid rgba(139, 92, 246, 0.1)",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              transition: "all 0.3s ease",
                            }}
                          >
                            <p
                              style={{
                                fontSize: "11px",
                                color: "#4B5563",
                                fontWeight: "600",
                                textTransform: "uppercase",
                                letterSpacing: "0.5px",
                                margin: 0,
                              }}
                            >
                              Tama Pay
                            </p>
                            <p
                              style={{
                                fontSize: "16px",
                                fontWeight: "800",
                                color: "#7C3AED",
                                margin: 0,
                              }}
                            >
                              $
                              {brandSummaryData?.outstanding_commitments?.tama_pay_total?.toLocaleString() ||
                                0}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div
                      className="grid 2xl:grid-cols-3 lg:grid-cols-3 xl:grid-cols-3 sm:grid-cols-1 md:grid-cols-1 gap-4 w-full"
                      style={{
                        height: "calc(100vh - 332px)",
                        minHeight: 0,
                        overflow: "hidden",
                      }}
                    >
                      <div
                        className="lg:col-span-2 xl:col-span-2 2xl:col-span-2 flex flex-col"
                        style={{
                          background:
                            "linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(240,253,244,0.9) 100%)",
                          backdropFilter: "blur(20px)",
                          WebkitBackdropFilter: "blur(20px)",
                          borderRadius: "16px",
                          border: "1px solid rgba(16, 185, 129, 0.15)",
                          paddingTop: "16px",
                          height: "100%",
                          overflow: "hidden",
                        }}
                      >
                        <div className="flex justify-between items-center px-4">
                          <h4
                            style={{
                              fontSize: "14px",
                              fontWeight: "700",
                              background:
                                "linear-gradient(135deg, #059669 0%, #047857 100%)",
                              WebkitBackgroundClip: "text",
                              WebkitTextFillColor: "transparent",
                              backgroundClip: "text",
                              margin: 0,
                            }}
                          >
                            Campaigns
                          </h4>
                          <div className="flex gap-1">
                            <YearToDate
                              list={ytdList}
                              selected={ytdList[0]}
                              onChange={changeMedium}
                            />
                            <button
                              onClick={downloadCsv}
                              className={`flex gap-2 h-[33px] px-5 justify-center items-center rounded-2xl text-sm  cursor-pointer transition-all duration-200 !bg-green-50 text-green-700
                              font-semibold shadow-sm border !border-green-200 mt-[-5px]
                            }`}
                            >
                              Download CSV
                              <Download color="#15803d" size={16} />
                            </button>
                          </div>
                        </div>
                        <div
                          className=" bg-white overflow-hidden w-full mt-2"
                          style={{
                            boxShadow: "0px 1px 3px 0px rgba(16, 24, 40, 0.08)",
                            border: "1px solid rgba(16, 185, 129, 0.1)",
                            display: "flex",
                            flexDirection: "column",
                            flex: 1,
                            minHeight: 0,
                            position: "relative",
                            zIndex: 1,
                          }}
                        >
                          <div
                            ref={tableContainerRef}
                            onScroll={handleScroll}
                            style={{
                              flex: 1,
                              overflowY: "auto",
                              overflowX: "auto",
                              scrollbarWidth: "thin",
                              scrollbarColor: "#e5e7eb transparent",
                            }}
                            className="dashboard-table-scroll"
                          >
                            <style>
                              {`
                              @media (min-width: 1024px) {
                                .dashboard-table-scroll {
                                  overflow-x: hidden !important;
                                }
                              }
                            `}
                            </style>
                            <Table
                              sx={{
                                width: "100%",
                                minWidth: {
                                  xs: 500,
                                  sm: 500,
                                  md: 500,
                                  lg: "100%",
                                },
                                borderCollapse: "separate",
                                borderSpacing: 0,
                                tableLayout: { xs: "fixed", lg: "auto" },
                              }}
                            >
                              <TableHead
                                sx={{
                                  position: "sticky",
                                  top: 0,
                                  zIndex: 10,
                                  background:
                                    "linear-gradient(180deg, #F8FAFC 0%, #F1F5F9 100%)",
                                  "& th": {
                                    fontWeight: 600,
                                    fontSize: "12px",
                                    color: "#475569",
                                    textTransform: "uppercase",
                                    letterSpacing: "0.08em",
                                    borderBottom: "2px solid #E2E8F0",
                                  },
                                }}
                              >
                                <TableRow>
                                  <TableCell
                                    sx={{
                                      py: 1.5,
                                      px: { xs: 1, sm: 1.5, md: 2 },
                                      textAlign: "left",
                                      width: { xs: "120px", lg: "25%" },
                                      minWidth: "120px",
                                    }}
                                  >
                                    Title
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      py: 1.5,
                                      px: { xs: 1, sm: 1.5, md: 2 },
                                      textAlign: "center",
                                      width: { xs: "100px", lg: "20%" },
                                      minWidth: "100px",
                                    }}
                                  >
                                    Date
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      py: 1.5,
                                      px: { xs: 1, sm: 1.5, md: 2 },
                                      textAlign: "left",
                                      width: { xs: "180px", lg: "35%" },
                                      minWidth: "180px",
                                    }}
                                  >
                                    Description
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      py: 1.5,
                                      px: { xs: 1, sm: 1.5, md: 2 },
                                      textAlign: "right",
                                      width: { xs: "100px", lg: "20%" },
                                      minWidth: "100px",
                                    }}
                                  >
                                    Budget
                                  </TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {isCampaignLoaded &&
                                dashboardCampaign?.length > 0 ? (
                                  <>
                                    {dashboardCampaign?.map((item, index) => {
                                      return (
                                        <DashboardItem key={index} {...item} />
                                      );
                                    })}
                                    {isLoadingMore && (
                                      <TableRow>
                                        <TableCell
                                          colSpan="4"
                                          style={{ textAlign: "center" }}
                                        >
                                          Loading more...
                                        </TableCell>
                                      </TableRow>
                                    )}
                                  </>
                                ) : (
                                  <TableRow>
                                    <TableCell
                                      colSpan={4}
                                      align="center"
                                      sx={{
                                        py: 6,
                                        color: "#6B7280",
                                        fontSize: "14px",
                                      }}
                                    >
                                      No records found
                                    </TableCell>
                                  </TableRow>
                                )}
                              </TableBody>
                            </Table>
                          </div>
                        </div>
                      </div>
                      <div
                        className="flex flex-col"
                        style={{
                          background: "white",
                          borderRadius: "16px",
                          border: "1px solid rgba(52, 211, 153, 0.15)",

                          height: "100%",
                          overflow: "hidden",
                        }}
                      >
                        <div
                          className="notification__title !px-4"
                          style={{
                            background:
                              "linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(240,253,244,0.9) 100%)",
                            paddingTop: "16px",
                            paddingBottom: "8px",
                          }}
                        >
                          <h4
                            style={{
                              fontSize: "14px",
                              fontWeight: "700",
                              background:
                                "linear-gradient(135deg, #10B981 0%, #059669 100%)",
                              WebkitBackgroundClip: "text",
                              WebkitTextFillColor: "transparent",
                              backgroundClip: "text",
                              margin: 0,
                            }}
                          >
                            Notifications
                          </h4>
                          {NotifModul.length > 0 && (
                            <button
                              className="button tertiary"
                              onClick={() =>
                                setModalNotification("notifications")
                              }
                            >
                              View all {arrowRight}
                            </button>
                          )}
                        </div>
                        <div
                          className=""
                          style={{
                            overflowY: "auto",
                            overflowX: "auto",
                            scrollbarWidth: "thin",
                            scrollbarColor: "#e5e7eb transparent",
                            display: "flex",
                            flexDirection: "column",
                            width: "100%",
                            backgroundColor: "white",
                          }}
                        >
                          {[...NotifModul]
                            .splice(0, NotifMax)
                            .map((item, index) => {
                              return (
                                <div key={index} className="">
                                  <BrandNotificationItem
                                    NotifModul={NotifModul}
                                    setNotifModul={setModalNotification}
                                    deleteNotif={deleteNotif}
                                    updateNotificationStatus={
                                      updateNotificationStatus
                                    }
                                    setModalNotification={setModalNotification}
                                    setModalNotificationData={
                                      setModalNotificationData
                                    }
                                    setModalData={() => {
                                      setModalNotificationData(item);
                                      setModalNotification("notifDetailDash");
                                    }}
                                    key={index}
                                    {...item}
                                    accountCount={accountCountNotification}
                                  />
                                </div>
                              );
                            })}
                          {showNotifPlacholder > 0 && (
                            <div
                              className={
                                "notification__placeholder " +
                                (showNotifPlacholder ===
                                (width <= 1180 ? "" : 2)
                                  ? "half "
                                  : "") +
                                (showNotifPlacholder === (width <= 1180 ? 2 : 3)
                                  ? "full "
                                  : "")
                              }
                            >
                              <div className="notification__placeholder-image">
                                <img
                                  src={
                                    process.env.PUBLIC_URL +
                                    "/images/placeholder/notificationPlaceholder.png"
                                  }
                                  alt="notificationPlaceholder.png"
                                />
                              </div>
                              <h6>
                                {showNotifPlacholder === (width <= 1180 ? 2 : 3)
                                  ? "You don’t have any notifications yet."
                                  : "That’s all the notifications for now!!"}
                              </h6>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
          {toastList && <Toast list={toastList} setList={setToastList} />}
        </>
      ) : (
        <>
          <NotSubscribe
            goToPricing={goToPricing}
            type="brand"
            setAuth={setAuth}
            setModal={setModal}
            NotifModul={NotifModul}
            setNotifModul={setModalNotification}
            profiles={profiles}
            closeCartPanel={closeCartPanel}
            user={user}
            cartItem={cartItem}
            title={"List"}
            background={"background_inflist.png"}
          ></NotSubscribe>
        </>
      )}
    </>
  );
}
